/**
 * steps/AccountSetupIntroduction.tsx
 * Account Setup Introduction for the onboarding wizard
 *
 * Introduces users to the account setup phase with:
 * - 7-card progressive educational flow
 * - Overview of account, assets, and broker configuration
 * - Scroll-based navigation with reading progress
 */

// 1. External dependencies
import React, { useState, useEffect, useRef, useCallback } from 'react';

// 2. Internal absolute imports
import { Card, CardHeader, CardBody, CardFooter, Button } from '@enfomentis/ui';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

/**
 * Custom hook for intersection observer
 */
const useIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: Partial<{
    threshold: number;
    root: Element | null;
    rootMargin: string;
  }>
) => {
  const [elements, setElements] = useState<Element[]>([]);

  useEffect(() => {
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(callback, {
      threshold: 0.3,
      ...options
    });

    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [elements, callback, options]);

  const addElement = useCallback((element: Element | null) => {
    if (element && !elements.includes(element)) {
      setElements(prev => [...prev, element]);
    }
  }, [elements]);

  return addElement;
};

/**
 * Account Setup Introduction component props
 */
interface AccountSetupIntroductionProps {
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}

/**
 * Account Setup Introduction component
 */
export const AccountSetupIntroduction: React.FC<AccountSetupIntroductionProps> = ({ goToNextStep, goToPreviousStep }) => {
  const { markCompleted } = useOnboardingData();

  // State for scroll-based engagement
  const [revealedSections, setRevealedSections] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Intersection observer callback
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const sectionId = entry.target.getAttribute('data-section-id');
        if (sectionId) {
          setRevealedSections(prev => new Set([...prev, sectionId]));
        }
      }
    });
  }, []);

  // Set up intersection observer
  const addToObserver = useIntersectionObserver(handleIntersection);

  // Show intro section immediately on mount
  useEffect(() => {
    setRevealedSections(prev => new Set([...prev, 'intro']));
  }, []);

  // Handler for "Learn More" - scrolls to second card
  const handleLearnMore = useCallback(() => {
    const secondCard = scrollContainerRef.current?.querySelector('[data-section-id="components"]');
    secondCard?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // Handler for skip/continue - marks complete and goes to next step
  const handleContinue = useCallback(() => {
    markCompleted('account-intro');
    goToNextStep();
  }, [markCompleted, goToNextStep]);

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-[100dvh] w-full flex flex-col border-0"
    >
      <CardBody className="flex-1 p-0">
        <div
          ref={scrollContainerRef}
          className="h-full overflow-y-auto px-6 scroll-smooth [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-default-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-0 scrollbar-thin scrollbar-thumb-default-300 scrollbar-track-transparent"
          style={{
            scrollSnapType: 'y mandatory',
            scrollBehavior: 'smooth'
          }}
        >

          {/* Card 1: Setting Up Your Trading Environment - with dual navigation */}
          <div
            data-section-id="intro"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 ${
              revealedSections.has('intro')
                ? 'opacity-100 translate-y-0'
                : 'opacity-100 translate-y-0'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Intro to Accounts, Assets, and Brokers
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Before you can track trades, we need to configure your trading environment - your account, the assets you trade, and your broker details.
                </p>
                <p className="text-base leading-relaxed text-text-secondary pt-4">
                  This foundation ensures <span className="text-primary">accurate profit & loss calculations</span> and meaningful performance analysis.
                </p>
              </CardBody>
              <CardFooter className="border-t-1 pt-6 pb-6 flex flex-col gap-4">
                <Button
                  color="primary"
                  size="lg"
                  className="w-full"
                  onPress={handleLearnMore}
                >
                  LEARN MORE
                </Button>
                <div className="flex justify-between items-center w-full">
                  <Button
                    variant="light"
                    onPress={goToPreviousStep}
                    startContent={<span>←</span>}
                    className="flex-1 justify-start"
                  >
                    Back
                  </Button>
                  <Button
                    variant="light"
                    onPress={handleContinue}
                    endContent={<span>→</span>}
                    className="flex-1 justify-end"
                  >
                    Skip Education
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 2: Three Components */}
          <div
            data-section-id="components"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-100 ${
              revealedSections.has('components')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Three Components of Your Trading Environment
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <div className="space-y-4 text-base leading-relaxed text-text-secondary pt-2">
                  <div>
                    <span className="text-primary font-semibold">1. Account Details</span> - What you're trading with (name, type, starting balance)
                  </div>
                  <div>
                    <span className="text-primary font-semibold">2. Assets Traded</span> - The instruments you trade (forex, stocks, crypto, etc.)
                  </div>
                  <div>
                    <span className="text-primary font-semibold">3. Broker Configuration</span> - Your broker and their fee structure
                  </div>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                Together, these components create a complete picture of your trading environment.
              </CardFooter>
            </Card>
          </div>

          {/* Card 3: What is an Account? */}
          <div
            data-section-id="account"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-200 ${
              revealedSections.has('account')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                What is an Account?
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Your account represents the trading capital you're working with. It could be a personal account, a prop firm challenge, or a funded account.
                </p>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                <div>Each account type has different rules and tracking needs - we'll capture <span className="text-primary">what's relevant for you.</span></div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 4: Assets You Trade */}
          <div
            data-section-id="assets"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-300 ${
              revealedSections.has('assets')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Assets You Trade
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Different assets have different characteristics - forex pairs have pips and lots, stocks have shares and dollars, crypto has volatile spreads.
                </p>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                <div>Knowing what you trade lets us show <span className="text-primary">the right metrics and calculations</span> for your instruments.</div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 5: Your Broker Configuration */}
          <div
            data-section-id="broker"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-400 ${
              revealedSections.has('broker')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Your Broker Configuration
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <div className="space-y-3 text-base leading-relaxed text-text-secondary pt-2">
                  <p>
                    Your broker charges fees - commissions, spreads, swap rates, and possibly platform fees.
                  </p>
                  <p>
                    These costs eat into your profits on every trade.
                  </p>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                <div>Accurate fee tracking means your <span className="text-primary">true P&L</span> reflects reality, not illusions.</div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 6: The Foundation of Accurate Tracking */}
          <div
            data-section-id="foundation"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-500 ${
              revealedSections.has('foundation')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                The Foundation of Accurate Tracking
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Without knowing your account, assets, and broker fees, we can't calculate your true performance. We'd just be tracking numbers without context.
                </p>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                <div>This setup ensures <span className="text-primary">every metric you see</span> is accurate, actionable, and meaningful.</div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 7: Ready to Set Up - with continue button */}
          <div
            data-section-id="ready"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-600 ${
              revealedSections.has('ready')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Let's Set Up Your Trading Environment
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  In the next few steps, you'll configure your account details, select the assets you trade, and set up your broker's fee structure.
                </p>
                <p className="text-primary text-base leading-relaxed pt-4">
                  This takes about 5 minutes, and you'll only need to do it once.
                </p>
              </CardBody>
              <CardFooter className="border-t-1 pt-6 pb-6">
                <Button
                  color="primary"
                  size="lg"
                  className="w-full"
                  onPress={handleContinue}
                >
                  Continue to Account Setup
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
